import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React, {memo} from 'react';
import parse from 'html-react-parser';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Link from 'next/link';
import {nftButtonSx} from '@/styles/nftButtonSx';
import {cdnUrl} from '@/util/cdnImage';
import {borderRadius, colors} from '@/styles/tokens';

// Migrated from globals.scss `.latest-cta-main-wrapper` (+ nested `.spectrum-header-wrapper
// .spectrum-heading`/`.solution-sub-heading`, + the `mobileScreen` (max-width:430px) padding
// override). Sole owner confirmed — no other component/page references this class; the nested
// heading/subheading rules are scoped by this ancestor and can only ever match inside this
// component's own render tree (same precedent as Lets-discuss/A1). `.blue-background-cta`, applied
// via the `className` prop by every one of this component's 269 current call sites, is left fully
// untouched and global in globals.scss — it is page-authored (passed in by ~81 pages), not this
// component's to own, per the Migration Policy's "never migrate a selector still consumed outside
// the current pass" rule.
const latestCtaMainWrapperSx = {
    backgroundImage: `url(${cdnUrl('Squeeze-offer-page/Cta-bg.webp')}) !important`,
    backgroundSize: 'cover !important',
    backgroundPosition: '60% !important',
    backgroundRepeat: 'no-repeat',
    padding: '30px 40px !important',
    borderRadius: '40px !important',
    '& .spectrum-header-wrapper .spectrum-heading': {
        fontSize: '28px !important',
        color: `${colors.text.inverse} !important`
    },
    '& .spectrum-header-wrapper .solution-sub-heading': {
        fontSize: '18px !important',
        color: `${colors.text.inverse} !important`
    },
    '@media (max-width: 430px)': {
        padding: '30px !important'
    }
} as const;

// Migrated from globals.scss `.latest-cta-button-wrapper` (+ `:hover`). Sole owner: this component's
// own CTA button. Merged with the pre-existing `nftButtonSx` (already applied here since commit
// 5503e10) in the same relative precedence the original globals.scss had: `.nft-button` (old line
// 970) was declared *before* `.latest-cta-button-wrapper` (old line 3553) in the plain-CSS cascade, so
// for every longhand both touched — background-color, border-color, color, in both the base and
// `:hover` states — `.latest-cta-button-wrapper`'s value won the tie (equal specificity, both
// `!important`, later source order wins). Listing `...nftButtonSx` first and this component's own
// values second reproduces that exact precedence as one merged declaration set.
//
// NOTE: this combined default is never visible in current production — all 269 real call sites carry
// `blue-background-cta` (see `latestCtaMainWrapperSx` above), whose higher-specificity compound
// selector (`.blue-background-cta .latest-cta-button-wrapper`, two classes, left untouched in
// globals.scss) always wins outright regardless of anything declared here. It is migrated — not
// deleted — because it is this component's own real fallback for any future consumer that doesn't
// pass that className; only its *source of truth* is relocating from globals.scss to this file, the
// declared values are unchanged.
const latestCtaButtonWrapperSx = {
    ...nftButtonSx,
    borderRadius: `${borderRadius.lg} !important`,
    backgroundColor: `${colors.text.inverse} !important`,
    borderColor: `${colors.text.inverse} !important`,
    color: `${colors.text.primary} !important`,
    '&:hover': {
        background: 'transparent !important',
        border: `1px solid ${colors.accent.orange} !important`,
        backgroundColor: '#f1f6ff !important', // no exact token match — preserved for parity
        borderColor: '#f1f6ff !important', // no exact token match — preserved for parity
        color: `${colors.text.primary} !important`
    }
} as const;

const LatestCTA = (props: any) => {
    const {heading, subheading, buttonText, openForm, className, meetingLink} = props;
    return (
        <Box>
            <MUIContainer>
                <Box className={`latest-cta-main-wrapper ${className}`} sx={latestCtaMainWrapperSx}>
                    <Grid container spacing={3}>
                        <Grid item lg={8}>
                            <Box className="spectrum-header-wrapper solution-header-wrapper solutions left ">
                                <Typography className="spectrum-heading color-black ">{heading}</Typography>
                                {subheading && (
                                    <Typography className="solution-sub-heading color-black data_links" pt={1.5}>
                                        {subheading ? parse(subheading) : ''}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                        <Grid item lg={4} display="flex" alignItems="center" justifyContent="center">
                            {meetingLink ? (
                                <Link target="_blank" href={openForm}>
                                    <MUIButton
                                        className="latest-cta-button-wrapper animated-button"
                                        sx={latestCtaButtonWrapperSx}
                                        variant="contained"
                                        label={buttonText}
                                    />
                                </Link>
                            ) : (
                                <MUIButton
                                    className="latest-cta-button-wrapper animated-button"
                                    sx={latestCtaButtonWrapperSx}
                                    variant="contained"
                                    label={buttonText}
                                    onClick={openForm}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(LatestCTA);
